import type { CompanyDetail } from '@planuze/admin-contracts';
import {
  Button,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useCompaniesContent } from '../../content/sections/companies.js';
import { fmtDate } from '../../lib/format.js';
import { FacetMultiSelect } from '../filters/facet-multi-select.js';
import { SearchInput } from '../search-input.js';

type Member = CompanyDetail['members'][number];

/* Aba de membros da empresa (ADR-0700+). Os membros já vêm carregados via useCompanyDetail;
   busca por email + faceta de papel são filtros client-side (useMemo) sobre esse array. */
export function CompanyMembersTab({ members }: { readonly members: readonly Member[] }) {
  const copy = useCompaniesContent();
  const [search, setSearch] = useState('');
  const [roles, setRoles] = useState<string[]>([]);

  /* Opções de papel deduplicadas a partir dos membros carregados. */
  const roleOptions = useMemo(
    () =>
      [...new Set(members.map((m) => m.role))]
        .sort((a, b) => a.localeCompare(b))
        .map((role) => ({ value: role, label: role })),
    [members],
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return members.filter((member) => {
      const matchesSearch = query === '' || member.email.toLowerCase().includes(query);
      const matchesRole = roles.length === 0 || roles.includes(member.role);
      return matchesSearch && matchesRole;
    });
  }, [members, search, roles]);

  if (members.length === 0) {
    return <EmptyState icon={<Users className="size-6" />} title={copy.membersTab.empty} />;
  }

  const clearFilters = (): void => {
    setSearch('');
    setRoles([]);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          ariaLabel={copy.membersTab.searchLabel}
          placeholder={copy.membersTab.searchPlaceholder}
        />
        <FacetMultiSelect
          label={copy.membersTab.roleFacetLabel}
          values={roles}
          onChange={setRoles}
          options={roleOptions}
        />
        <span className="text-sm tabular-nums text-muted-foreground">
          {copy.common.countOf(filtered.length, members.length)}
        </span>
      </div>
      {filtered.length === 0 ? (
        <EmptyState
          icon={<Users className="size-6" />}
          title={copy.common.noResultsTitle}
          description={copy.membersTab.emptyFilteredDescription}
          primaryAction={
            <Button variant="outline" onClick={clearFilters}>
              {copy.common.clearFilters}
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{copy.membersTab.columnEmail}</TableHead>
              <TableHead>{copy.membersTab.columnRole}</TableHead>
              <TableHead>{copy.membersTab.columnJoinedAt}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((member) => (
              <TableRow key={member.userId}>
                <TableCell className="text-foreground">{member.email}</TableCell>
                <TableCell className="text-muted-foreground">{member.role}</TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(member.joinedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
